import {isDeckId} from './remote-room';
import {parseDeck} from './parser';

export type SavedAsset = {url: string; kind: 'image' | 'video'};
export type DeckContent = {source: string; assets: Record<string, SavedAsset>; baseDeckId: string};
export type DeckRevision = DeckContent & {revision: number; updatedAt: number};
export type SharedDeck = DeckRevision & {
  id: string;
  original: DeckContent;
  history: Array<{revision: number; updatedAt: number}>;
};
export type DeckWrite = DeckContent & {baseRevision: number; originalSource: string};
export const MAX_SOURCE_BYTES = 96_000;
export const MAX_DECK_BODY = 512_000;
export const MAX_UPLOAD_BYTES = 64 * 1024 * 1024;

export function validAssetName(name: string): boolean {
  return name.length > 0 && name.length <= 240 && !/[\\\x00-\x1f]/.test(name) &&
    name.split('/').every(part => !!part && part !== '.' && part !== '..');
}

/** Same format on both sides. No executable HTML or arbitrary object URLs. */
export function parseDeckWrite(raw: unknown): DeckWrite | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as DeckWrite;
  if (!Number.isSafeInteger(value.baseRevision) || value.baseRevision < 0 || !isDeckId(value.baseDeckId)) return null;
  for (const source of [value.source, value.originalSource]) {
    if (typeof source !== 'string' || new TextEncoder().encode(source).length > MAX_SOURCE_BYTES) return null;
    try { if (!parseDeck(source).slides.length) return null; } catch { return null; }
  }
  if (!value.assets || typeof value.assets !== 'object' || Array.isArray(value.assets)) return null;
  const assets: Record<string, SavedAsset> = Object.create(null);
  if (Object.keys(value.assets).length > 500) return null;
  for (const [name, asset] of Object.entries(value.assets)) {
    if (!validAssetName(name) || !asset || !['image', 'video'].includes(asset.kind) || typeof asset.url !== 'string') return null;
    if (!/^\/media\/uploads\/[a-f0-9]{64}$/.test(asset.url)) return null;
    assets[name] = {url: asset.url, kind: asset.kind};
  }
  return {source: value.source, originalSource: value.originalSource, baseDeckId: value.baseDeckId, baseRevision: value.baseRevision, assets};
}

export function sameContent(left: DeckContent, right: DeckContent): boolean {
  return left.source === right.source && left.baseDeckId === right.baseDeckId &&
    JSON.stringify(Object.entries(left.assets).sort()) === JSON.stringify(Object.entries(right.assets).sort());
}

/** CAS, including idempotent retry when a successful response was lost. */
export function canSave(current: DeckRevision | null, incoming: DeckWrite): boolean {
  return (current?.revision ?? 0) === incoming.baseRevision || !!current && sameContent(current, incoming);
}
